// Types
import { get } from "lodash";
import { Action } from "../actions/types";
import {
  EMPLOYEE_BALANCES,
  EMPLOYEE_PLAN,
  EMPLOYEE_STATS,
} from "./utilities/employee/employee.constant";
import { IEmployeeStats } from "./utilities/employee/employee.interface";
import {
  CraHsaParser,
  storeCRAInfoLocalStorageSuccess,
} from "./utilities/employee/employee.cra.hsa.helper";
import moment from "moment";
import { kycPayloadType } from "../components/dashboard/kyc/utilities/kyc.types";

type IDeletionInfo = {
  fetchTriggered: boolean;
  cancelDeletionTriggered: boolean;
  hasErrors: boolean;
  isTriggered: unknown;
  initiatedAt: string;
  deletionDate: string;
  deletionGracePeriod: number;
  deletionSuccess: boolean;
  verificationCodeSuccess: boolean;
  cancelVerificationSuccess: boolean;
  cancelVerificationFailed: boolean;
  fetchDeletionInfoFailed: boolean;
};

type IKycDetails = {
  loading: boolean;
  kycStatus: string;
  additionalKycStatus: boolean;
  kycStatusMessage: string;
  genderList: Array<string>;
  countryList: Array<string>;
  provinceList: Array<string>;
  identityTypeList: Array<string>;
  occupationList: Array<{ id: number; name: string } | string>;
  upsertSucceeded: boolean;
  upsertFailed: boolean;
  upsertFailedMsg: string;
  inviteUpdated: boolean;
  kycPayloadReady: boolean;
  payload: kycPayloadType;
};

// State of the KYC application that a super admin submits for a plan member.
// It is separate from IKycDetails because IKycDetails holds the KYC data of the
// signed-in user. The two flows must not overwrite each other.
type IAdminKyc = {
  loading: boolean;
  personGuid: string;
  personData: any;
  personFetchFailed: boolean;
  kycStatus: string;
  additionalKycStatus: boolean;
  statusFetchFailed: boolean;
  upsertSucceeded: boolean;
  upsertFailed: boolean;
  upsertFailedMsg: string;
  upsertFailedStatus: number;
};

type IAdminDependent = {
  guid: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
  relation: string;
  fullTimeStudent: boolean;
  disabled: boolean;
};

type IAdminDependents = {
  loading: boolean;
  dependents: IAdminDependent[];
  fetchFailed: boolean;
  createSucceeded: boolean;
  createFailed: boolean;
  createFailedMsg: string;
  deleteSucceeded: boolean;
  deleteFailed: boolean;
  deleteFailedMsg: string;
};

type IAdminCard = {
  loading: boolean;
  personGuid: string;
  cards: Array<any>;
  awaitingIssuerConfirmation: boolean;
  fetchFailed: boolean;
  statusUpdateSucceeded: boolean;
  statusUpdateFailed: boolean;
  statusUpdateFailedMsg: string;
  reissueSucceeded: boolean;
  reissueFailed: boolean;
  reissueFailedMsg: string;
  reissueMessage: string;
  replacementLast4Digits: string;
  replacementOnFile: boolean;
  issueSucceeded: boolean;
  issueFailed: boolean;
  issueFailedMsg: string;
  issuedLast4Digits: string;
};

type IFlexAllocationInfo = {
  apiResponse: any;
};

type IUpdateStatus = {
  success: boolean;
  personGuid: string;
  failed: boolean;
  properties: any;
};

type IContributionInfo = {
  apiResponse: any;
  updateStatus: IUpdateStatus;
};

type IUpdatedAllocationInfo = {
  apiResponse: any;
  updateStatus: IUpdateStatus;
};

// State types
type State = {
  employees: Array<any>;
  data: any;
  employeesFull: Array<any>;
  planDetails: any;
  planAdminPlanDetails: any;
  hsaWsa: any;
  employeeDetails: any;
  hsaDetails: any;
  wsaDetails: any;
  flexDetails: any;
  employeeStats: IEmployeeStats;
  employeeTerminationStats: Array<any>;
  employeeCards: Array<any>;
  employeeCraHsaInfo: any;
  employeeCraParsedAt: string;
  pinResetCompleted: string;
  accountDeletion: IDeletionInfo;
  fullEmployeeCount: number;
  flexAllocationInfo: IFlexAllocationInfo;
  contributionInfo: IContributionInfo;
  updatedAllocationInfo: IUpdatedAllocationInfo;
  kycDetails: IKycDetails;
  adminKyc: IAdminKyc;
  adminCard: IAdminCard;
  adminDependents: IAdminDependents;
  personAddressOptions: {
    loading: boolean;
    responsePayload: any;
    hasErrors: boolean;
    errorPayload: string | null;
    isValidated: boolean;
  };
};

// Initial state
export const INITIAL_STATE: State = {
  employees: [],
  data: {},
  employeesFull: [],
  employeeStats: EMPLOYEE_STATS,
  planDetails: {},
  planAdminPlanDetails: {},
  hsaWsa: {},
  employeeDetails: {},
  hsaDetails: {},
  wsaDetails: {},
  flexDetails: {},
  employeeTerminationStats: [],
  employeeCards: [],
  employeeCraHsaInfo: {},
  employeeCraParsedAt: "",
  pinResetCompleted: "",
  accountDeletion: {
    fetchTriggered: false,
    cancelDeletionTriggered: false,
    hasErrors: false,
    isTriggered: false,
    initiatedAt: "",
    deletionDate: "",
    deletionGracePeriod: 0,
    deletionSuccess: false,
    verificationCodeSuccess: false,
    cancelVerificationSuccess: false,
    cancelVerificationFailed: false,
    fetchDeletionInfoFailed: false,
  },
  fullEmployeeCount: 0,
  flexAllocationInfo: {
    apiResponse: null,
  },
  contributionInfo: {
    apiResponse: null,
    updateStatus: {
      success: false,
      personGuid: "",
      failed: false,
      product: "",
    },
  },
  updatedAllocationInfo: {
    apiResponse: null,
    updateStatus: {
      success: false,
      personGuid: "",
      failed: false,
      properties: {},
    },
  },
  kycDetails: {
    loading: false,
    kycStatus: "",
    additionalKycStatus: false,
    kycStatusMessage: "",
    genderList: [],
    countryList: [],
    provinceList: [],
    identityTypeList: [],
    occupationList: [],
    upsertSucceeded: false,
    upsertFailed: false,
    upsertFailedMsg: "",
    inviteUpdated: false,
    kycPayloadReady: false,
    payload: {
      documentNumber: "",
      gender: "",
      identityCountryId: "",
      identityProvinceId: "",
      identityType: "",
      mobCountry: "",
      mobileNumber: "",
      personAddress1: "",
      personAddress2: "",
      personCity: "",
      personCountry: "",
      personGuid: "",
      personPostalCode: "",
      personProvince: "",
    },
  },
  adminKyc: {
    loading: false,
    personGuid: "",
    personData: {},
    personFetchFailed: false,
    kycStatus: "",
    additionalKycStatus: false,
    statusFetchFailed: false,
    upsertSucceeded: false,
    upsertFailed: false,
    upsertFailedMsg: "",
    upsertFailedStatus: 0,
  },
  adminDependents: {
    loading: false,
    dependents: [],
    fetchFailed: false,
    createSucceeded: false,
    createFailed: false,
    createFailedMsg: "",
    deleteSucceeded: false,
    deleteFailed: false,
    deleteFailedMsg: "",
  },
  adminCard: {
    loading: false,
    personGuid: "",
    cards: [],
    awaitingIssuerConfirmation: false,
    fetchFailed: false,
    statusUpdateSucceeded: false,
    statusUpdateFailed: false,
    statusUpdateFailedMsg: "",
    reissueSucceeded: false,
    reissueFailed: false,
    reissueFailedMsg: "",
    reissueMessage: "",
    replacementLast4Digits: "",
    replacementOnFile: false,
    issueSucceeded: false,
    issueFailed: false,
    issueFailedMsg: "",
    issuedLast4Digits: "",
  },
  personAddressOptions: {
    loading: false,
    responsePayload: {},
    hasErrors: false,
    errorPayload: null,
    isValidated: false,
  },
};

export default (state: State = INITIAL_STATE, action: Action): State => {
  switch (action.type) {
    case "CREATE_EMPLOYEE":
      return { ...state, data: { ...state.data, ...action.data } };
    case "EDIT_EMPLOYEE":
      return { ...state, data: { ...state.data, ...action.data } };
    case "UPDATE_FULL_EMPLOYEES":
      return {
        ...state,
        employeesFull: { ...action.data.employeeList },
        employeeStats: action.data.employeeStats
          ? { ...action.data.employeeStats }
          : state.employeeStats,
        fullEmployeeCount: action.data.fullEmployeeCount ?? 0,
      };
    case "CLEAR_FULL_EMPLOYEES":
      return {
        ...state,
        employeesFull: INITIAL_STATE.employeesFull,
        employeeStats: INITIAL_STATE.employeeStats,
      };
    case "UPDATE_PLAN_DETAILS":
      return { ...state, planDetails: { ...action.data } };
    case "UPDATE_PLANADMIN_PLAN_DETAILS":
      return { ...state, planAdminPlanDetails: { ...action.data } };
    case "UPDATE_PLAN_HSA_WSA":
      let hsaDetails = {
        PlanType: null,
        accountguid: null,
        availablebalance: 0,
        availablebalanceexpirydate: null,
        ayaproductname: null,
        carryoveryear1: 0,
        carryoveryear1expirydate: null,
        carryoveryear2: 0,
        carryoveryear2expirydate: null,
        daysToSubmitClaimPreviousYear: null,
        gracebalance: 0,
        gracebalanceexpirydate: null,
        nextcontributiondate: null,
        openingbalance: 0,
        personguid: null,
        planyearbegin: null,
        planyearend: null,
        prepaid: null,
        totalUsed: 0,
        contributionYearAmount: 0,
      };
      let wsaDetails = {
        PlanType: null,
        accountguid: null,
        availablebalance: 0,
        availablebalanceexpirydate: null,
        ayaproductname: null,
        carryoveryear1: 0,
        carryoveryear1expirydate: null,
        carryoveryear2: 0,
        carryoveryear2expirydate: null,
        daysToSubmitClaimPreviousYear: null,
        gracebalance: 0,
        gracebalanceexpirydate: null,
        nextcontributiondate: null,
        openingbalance: 0,
        personguid: null,
        planyearbegin: null,
        planyearend: null,
        prepaid: null,
        totalUsed: 0,
        contributionYearAmount: 0,
      };
      let flexDetails = {
        PlanType: null,
        accountguid: null,
        availablebalance: 0,
        availablebalanceexpirydate: null,
        ayaproductname: null,
        carryoveryear1: 0,
        carryoveryear1expirydate: null,
        carryoveryear2: 0,
        carryoveryear2expirydate: null,
        daysToSubmitClaimPreviousYear: null,
        gracebalance: 0,
        gracebalanceexpirydate: null,
        nextcontributiondate: null,
        openingbalance: 0,
        personguid: null,
        planyearbegin: null,
        planyearend: null,
        prepaid: null,
        totalUsed: 0,
        contributionYearAmount: 0,
      };
      action.data?.forEach((item) => {
        if (item.ayaproductname.includes("HSA"))
          hsaDetails = {
            ...item,
            prepaid: item.ayaproductname.includes("Prepaid"),
          };
        if (item.ayaproductname.includes("WSA"))
          wsaDetails = {
            ...item,
            prepaid: item.ayaproductname.includes("Prepaid"),
          };
        if (item.ayaproductname.includes("Flex")) flexDetails = item;
      });
      return {
        ...state,
        hsaWsa: {
          ...action.data,
        },
        hsaDetails,
        wsaDetails,
        flexDetails,
      };
    case "UPDATE_EMPLOYEES":
      return { ...state, employees: { ...action.data } };
    case "CLEAR_EMPLOYEE":
      return {
        ...state,
        data: INITIAL_STATE.data,
        planDetails: INITIAL_STATE.planDetails,
        hsaWsa: INITIAL_STATE.hsaWsa,
        employeeDetails: INITIAL_STATE.employeeDetails,
      };
    case "STORE_EMPLOYEE_DETAILS":
      return { ...state, employeeDetails: { ...action.data } };
    case "UPDATE_EMPLOYEE_TERMINATION_STATS":
      return {
        ...state,
        employeeTerminationStats: [...action.data],
      };
    case "CLEAR_EMPLOYEE_TERMINATION_STATS":
      return {
        ...state,
        employeeTerminationStats: INITIAL_STATE.employeeTerminationStats,
      };
    case "UPDATE_EMPLOYEE_CRA_HSA_INFO":
      const parsedJsonValues: Array<any> = CraHsaParser(action.data);
      let parsedAt = "";
      if (parsedJsonValues.length > 0) {
        parsedAt = moment(new Date()).format("LL");
      }
      let craInfoStored = false;
      if (typeof Storage !== "undefined") {
        craInfoStored = storeCRAInfoLocalStorageSuccess(
          parsedJsonValues,
          parsedAt
        );
      }
      if (craInfoStored) {
        return {
          ...state,
          employeeCraHsaInfo: INITIAL_STATE.employeeCraHsaInfo,
        };
      }
      return {
        ...state,
        employeeCraHsaInfo: {
          medicalExpenseList: [...parsedJsonValues],
          employeeCraParsedAt: parsedAt,
        },
      };
    case "CLEAR_EMPLOYEE_CRA_HSA_INFO":
      return {
        ...state,
        employeeCraHsaInfo: INITIAL_STATE.employeeCraHsaInfo,
      };
    case "UPDATE_EMPLOYEE_CARDS":
      return {
        ...state,
        employeeCards: [...action.data],
      };
    case "CLEAR_EMPLOYEE_CARDS":
      return {
        ...state,
        employeeCards: [],
      };
    case "SET_PIN_RESET_COMPLETED":
      return {
        ...state,
        pinResetCompleted: action.data.pinResetCompleted,
      };
    case "UPDATE_ACC_DELETION_INFO":
      return {
        ...state,
        accountDeletion: {
          ...state.accountDeletion,
          ...action.data,
        },
      };
    case "CLEAR_ACC_DELETION_INFO":
      return {
        ...state,
        accountDeletion: INITIAL_STATE.accountDeletion,
      };
    case "SET_EMPLOYEE_CONTRIBUTION_RESPONSE":
      return {
        ...state,
        contributionInfo: {
          ...state.contributionInfo,
          apiResponse: action.data.apiResponse,
          updateStatus: { ...action.data.updateStatus },
        },
      };
    case "UPDATE_FULL_EMPLOYEES_CONTRIBUTION":
      if (action?.data?.payload.length === 0) {
        return { ...state };
      }
      const calledBy = action?.data?.calledBy;

      let updatedEmployeeList: any = state.employeesFull;

      action?.data?.payload?.forEach((respData: any) => {
        const product = get(respData, "product", "");
        let propKey = "hsaAmount";
        if (product === "WSA") {
          propKey = "wsaAmount";
        }
        if (product === "Flex") {
          propKey = "flexAmount";
        }

        if (calledBy === "PA") {
          Object.keys(updatedEmployeeList).forEach((key: any) => {
            let employee = state.employeesFull[key];
            if (
              employee?.personGuid &&
              employee?.personGuid === respData.personGuid
            ) {
              employee[propKey] = respData.contribution;
            }
          });
        } else {
          updatedEmployeeList.listData?.forEach((employee: any) => {
            if (
              employee?.employeeGuid &&
              employee?.employeeGuid === respData.personGuid
            ) {
              employee[propKey] = respData.contribution;
            }
          });
        }
      });

      return {
        ...state,
        employeesFull: { ...updatedEmployeeList },
      };
    case "CLEAR_CONTRIBUTION_DATA":
      return {
        ...state,
        contributionInfo: INITIAL_STATE.contributionInfo,
      };
    case "SET_EMPLOYEE_FLEX_ALLOCATION_API_RESPONSE":
      return {
        ...state,
        flexAllocationInfo: {
          ...state.flexAllocationInfo,
          apiResponse: action.data,
        },
      };
    case "SET_EMPLOYEE_UPDATE_ALLOCATION_RESPONSE":
      return {
        ...state,
        updatedAllocationInfo: {
          ...state.updatedAllocationInfo,
          apiResponse: action.data.apiResponse,
          updateStatus: { ...action.data.updateStatus },
        },
      };

    case "UPDATE_FULL_EMPLOYEES_ALLOCATION_DETAILS":
      if (action?.data?.payload.persons.length === 0) {
        return { ...state };
      }

      let updatedEmployeeListWithAllocation: any = state.employeesFull;
      if (
        state.employeesFull?.type &&
        state.employeesFull?.type === "SaEmployeeViewList"
      ) {
        updatedEmployeeListWithAllocation = state.employeesFull?.listData;
      }

      action?.data?.payload?.persons?.forEach((person: any) => {
        Object.keys(updatedEmployeeListWithAllocation).forEach((key: any) => {
          let employee =
            state.employeesFull?.type === "SaEmployeeViewList"
              ? state.employeesFull?.listData[key]
              : state.employeesFull[key];
          if (
            (employee?.employeeGuid &&
              employee?.employeeGuid === person.personGuid) ||
            (employee?.personGuid && employee?.personGuid === person.personGuid)
          ) {
            let familyType = "Single";
            if (person.familyType === "Single+1") {
              familyType = "SinglePlusOne";
            } else if (person.familyType === "Single+2") {
              familyType = "SinglePlusTwo";
            }

            const hsaAmount =
              action?.data?.payload.allocation["HSA"][familyType];
            const wsaAmount =
              action?.data?.payload.allocation["WSA"][familyType];
            const flexAmount =
              action?.data?.payload.allocation["Flex"][familyType];

            employee = {
              ...employee,
              ...{
                className: person.className,
                familyType: person.familyType,
                hsaAmount,
                wsaAmount,
                flexAmount,
              },
            };
            updatedEmployeeListWithAllocation[key] = { ...employee };
          }
        });
      });

      if (
        state.employeesFull?.type &&
        state.employeesFull?.type === "SaEmployeeViewList"
      ) {
        return {
          ...state,
          employeesFull: {
            listData: [...updatedEmployeeListWithAllocation],
          },
        };
      }

      return {
        ...state,
        employeesFull: { ...updatedEmployeeListWithAllocation },
      };

    case "CLEAR_UPDATE_EMPLOYEE_ALLOCATION_DATA":
      return {
        ...state,
        updatedAllocationInfo: INITIAL_STATE.updatedAllocationInfo,
      };
    case "UPDATE_KYC_DETAILS":
      return {
        ...state,
        kycDetails: {
          ...state.kycDetails,
          ...action.data,
        },
      };
    case "RESET_KYC_DETAILS":
      return {
        ...state,
        kycDetails: INITIAL_STATE.kycDetails,
      };
    case "UPDATE_ADMIN_KYC":
      return {
        ...state,
        adminKyc: {
          ...state.adminKyc,
          ...action.data,
        },
      };
    case "RESET_ADMIN_KYC":
      return {
        ...state,
        adminKyc: INITIAL_STATE.adminKyc,
      };
    case "UPDATE_ADMIN_CARD":
      return {
        ...state,
        adminCard: {
          ...state.adminCard,
          ...action.data,
        },
      };
    case "RESET_ADMIN_CARD":
      return {
        ...state,
        adminCard: INITIAL_STATE.adminCard,
      };
    case "UPDATE_ADMIN_DEPENDENTS":
      return {
        ...state,
        adminDependents: {
          ...state.adminDependents,
          ...action.data,
        },
      };
    case "RESET_ADMIN_DEPENDENTS":
      return {
        ...state,
        adminDependents: INITIAL_STATE.adminDependents,
      };
    case "UPDATE_PERSON_ADDRESS_OPTIONS":
      return {
        ...state,
        personAddressOptions: {
          ...state.personAddressOptions,
          ...action.data,
        },
      };
    case "UPDATE_ADDRESS_VALIDATED_FLAG":
      return {
        ...state,
        personAddressOptions: {
          ...state.personAddressOptions,
          isValidated: action.data,
        },
      };
    case "CLEAR_ADDRESS_SUGGESTIONS":
      return {
        ...state,
        personAddressOptions: {
          ...state.personAddressOptions,
          responsePayload: null,
          loading: false,
          hasErrors: false,
          errorPayload: null,
        },
      };
    default:
      return state;
  }
};
